// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type I18NextResources } from '@shared/ui-kit/providers/translation-provider/i18next-config';
import { type TFunction } from 'i18next';

/** Array of popular US index ticker symbols */
const popularUsIndexes = ['SPY', 'IWB', 'QQQ', 'SNPE', 'IWM', 'IWV', 'VTI'];

/** Array of popular international index ticker symbols */
const popularInternationalIndexes = [
  'FEZ',
  'VGK',
  'DAX',
  'EWQ',
  'EWP',
  'EWJ',
  'EWZ',
];

/** Array of popular thematic ETF ticker symbols */
const popularThematics = [
  'ACES',
  'ARKK',
  'CIBR',
  'XSD',
  'AQWA',
  'METV',
  'XBI',
  'IDRV',
];

/** A map with ticker symbols as keys and their formatted names as values. */
export const formattedBenchmarksRegistry = new Map(
  Object.entries({
    ACES: 'Clean Energy',
    AQWA: 'Water',
    ARKK: 'Innovation',
    CIBR: 'Cybersecurity',
    DGRW: 'Dividends',
    EATZ: 'Food & Beverage',
    IDRV: 'Self-Driving',
    IFRA: 'Infrastructure',
    IWB: 'Russell 1000',
    IWM: 'Russell 2000',
    IWV: 'Russell 3000',
    LCTU: 'Carbon',
    METV: 'Metaverse',
    PBJ: 'Fast Food',
    QQQ: 'Nasdaq 100',
    SNPE: 'S&P 500 ESG',
    SNSR: 'Internet of Things',
    SPY: 'S&P 500',
    FEZ: 'Europe Large-Cap',
    VTI: 'US Total Market',
    XBI: 'Biotechnology',
    XLB: 'Materials',
    XLC: 'Communication Services',
    XLE: 'Energy',
    XLF: 'Financials',
    XLI: 'Industrials',
    XLK: 'Technology',
    XLP: 'Consumer staples',
    XLRE: 'Real estate',
    XLU: 'Utilities',
    XLV: 'Healthcare',
    XLY: 'Consumer Discretionary',
    XRT: 'Lifestyle & Luxury',
    XSD: 'Semiconductors',
    YOLO: 'Marijuana',
    HLAL: 'USA Shariah',
    DAX: 'Germany',
    EWP: 'Spain',
    VGK: 'Europe Total Market',
    EWU: 'United Kingdom',
    EWQ: 'France',
    EWI: 'Italy',
    EWN: 'Netherlands',
    EWD: 'Sweden',
    EDEN: 'Denmark',
    EWH: 'Hong Kong',
    EWA: 'Australia',
    EWC: 'Canada',
    EWZ: 'Brazil',
    EWW: 'Mexico',
    ECH: 'Chile',
    IKOR: 'Korea',
    EWJ: 'Japan',
    NDIA: 'India',
  }),
);

/**
 * Categorizes a benchmark based on its ticker symbol.
 *
 * @param ticker - The ticker symbol of the benchmark to categorize.
 * @param t - An instance of the {@link TFunction} to translate category names using
 *   i18next.
 * @returns The category name of the benchmark.
 */
export const categorizeBenchmark = (
  ticker: string,
  t: TFunction<keyof I18NextResources, undefined>,
): string => {
  if (popularUsIndexes.includes(ticker)) {
    return t('benchmark-selector.categories.popular-us-indexes', {
      ns: 'components',
    });
  }

  if (popularInternationalIndexes.includes(ticker)) {
    return t('benchmark-selector.categories.popular-international-indexes', {
      ns: 'components',
    });
  }

  if (popularThematics.includes(ticker)) {
    return t('benchmark-selector.categories.popular-thematics', {
      ns: 'components',
    });
  }

  // Sector ETFs
  if (ticker.startsWith('XL')) {
    return t('benchmark-selector.categories.sectors', { ns: 'components' });
  }

  // ESG ETFs
  if (['LCTU', 'HLAL'].includes(ticker)) {
    return t('benchmark-selector.categories.esg', { ns: 'components' });
  }

  return t('benchmark-selector.categories.other', { ns: 'components' });
};
